import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminApi } from '../../api/settings'
import { PageSpinner } from '../../components/ui/Spinner'
import { Modal } from '../../components/ui/Modal'
import { formatDate } from '../../lib/utils'
import { Plus, Building2, Trash2, RotateCcw } from 'lucide-react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'

/**
 * Admin → Companies. Lifted from the previous /admin page (Admin.tsx)
 * into the new AdminLayout shell. Logout + user identity now live in
 * AdminSidebar's footer, so the per-page header was trimmed back to just
 * the title + Create button.
 */
export function CompaniesPage() {
  useEffect(() => { document.title = 'Companies' }, [])
  const navigate = useNavigate()
  const [showCreate, setShowCreate] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null)
  const qc = useQueryClient()

  const { data: companies, isLoading } = useQuery({
    queryKey: ['admin-companies'],
    queryFn: () => adminApi.listCompanies().then(r => r.data),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteCompany(id),
    onSuccess: () => {
      toast.success('Company deactivated')
      qc.invalidateQueries({ queryKey: ['admin-companies'] })
      setDeleteTarget(null)
    },
    onError: () => toast.error('Failed to deactivate company'),
  })

  const reactivateMutation = useMutation({
    mutationFn: (id: string) => adminApi.reactivateCompany(id),
    onSuccess: () => {
      toast.success('Company reactivated')
      qc.invalidateQueries({ queryKey: ['admin-companies'] })
    },
    onError: () => toast.error('Failed to reactivate company'),
  })

  if (isLoading) return <PageSpinner />

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between gap-4">
        <h1 className="page-title">Companies</h1>
        <button onClick={() => setShowCreate(true)} className="btn btn-primary btn-sm">
          <Plus className="w-3.5 h-3.5" /> New Company
        </button>
      </div>

      <div className="card overflow-hidden">
        <table className="table">
          <thead>
            <tr>
              <th>Company</th><th>Status</th><th>Created</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(!companies || companies.length === 0) && (
              <tr><td colSpan={4} className="text-center py-8 text-gray-500 text-sm">No companies</td></tr>
            )}
            {companies?.map((c: { id: string; name: string; isActive: boolean; createdAt: string }) => (
              <tr key={c.id}>
                <td>
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded bg-gray-100 flex items-center justify-center">
                      <Building2 className="w-4 h-4 text-gray-500" />
                    </div>
                    <span className="font-medium">{c.name}</span>
                  </div>
                </td>
                <td>
                  <span className={`badge ${c.isActive ? 'badge-green' : 'badge-gray'}`}>
                    {c.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="text-gray-500 text-xs">{formatDate(c.createdAt)}</td>
                <td>
                  <div className="flex justify-end gap-1">
                    <button
                      onClick={() => navigate(`/company/${c.id}/leads`)}
                      className="btn btn-ghost btn-sm text-xs text-primary-600"
                    >
                      Open CRM
                    </button>
                    {c.isActive ? (
                      <button
                        onClick={() => setDeleteTarget({ id: c.id, name: c.name })}
                        className="btn btn-ghost btn-sm text-xs text-red-600 hover:bg-red-50"
                        aria-label="Deactivate company"
                        title="Deactivate company"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        <span className="ml-1">Deactivate</span>
                      </button>
                    ) : (
                      <button
                        onClick={() => reactivateMutation.mutate(c.id)}
                        disabled={reactivateMutation.isPending}
                        className="btn btn-ghost btn-sm text-xs text-green-700 hover:bg-green-50"
                        aria-label="Reactivate company"
                        title="Reactivate company"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                        <span className="ml-1">Reactivate</span>
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="New Company" size="sm">
        <CreateCompanyForm
          onSuccess={() => { setShowCreate(false); qc.invalidateQueries({ queryKey: ['admin-companies'] }) }}
        />
      </Modal>

      <Modal
        open={!!deleteTarget}
        onClose={() => { if (!deleteMutation.isPending) setDeleteTarget(null) }}
        title="Deactivate company"
        size="md"
      >
        {deleteTarget && (
          <div className="space-y-3 text-sm">
            <p className="text-gray-700">
              You are about to deactivate <span className="font-semibold">{deleteTarget.name}</span>.
            </p>
            <p className="text-gray-700">
              The following will become inaccessible from the application:
            </p>
            <p className="text-xs text-gray-600 leading-relaxed">
              Leads, Deals, Contacts, Organizations, Users, Activities, Notes, Comments, Tasks,
              Call logs, Email accounts/messages/templates, Custom fields &amp; statuses, Custom views,
              Lead sources, Assignment rules, SLA policies, WhatsApp settings, Notifications.
            </p>
            <p className="text-xs text-gray-500">
              Data is preserved in the database. You can reactivate the company later
              from the admin list.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleteMutation.isPending}
                className="btn btn-secondary btn-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate(deleteTarget.id)}
                disabled={deleteMutation.isPending}
                className="btn btn-sm bg-red-600 hover:bg-red-700 text-white border-red-600"
              >
                {deleteMutation.isPending ? 'Deactivating...' : 'Deactivate'}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

function CreateCompanyForm({ onSuccess }: { onSuccess: () => void }) {
  const { register, handleSubmit, reset, watch, formState: { errors, isValid } } = useForm<{
    name: string; ownerEmail: string; ownerFirstName: string; ownerLastName: string; ownerPassword?: string
  }>({ mode: 'onChange' })

  // Debounce the name field for the async availability check.
  const nameValue = watch('name') ?? ''
  const trimmedName = nameValue.trim()
  const [debouncedName, setDebouncedName] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setDebouncedName(trimmedName), 400)
    return () => clearTimeout(t)
  }, [trimmedName])

  const nameCheck = useQuery({
    queryKey: ['admin-check-name', debouncedName],
    queryFn: () => adminApi.checkCompanyName(debouncedName).then(r => r.data.available),
    enabled: debouncedName.length >= 2,
    staleTime: 30_000,
    retry: false,
  })

  const nameStatus: 'idle' | 'tooShort' | 'checking' | 'available' | 'taken' | 'error' =
    trimmedName.length === 0 ? 'idle'
    : trimmedName.length < 2 ? 'tooShort'
    : trimmedName !== debouncedName || nameCheck.isFetching ? 'checking'
    : nameCheck.isError ? 'error'
    : nameCheck.data === true ? 'available'
    : nameCheck.data === false ? 'taken'
    : 'checking'

  const mutation = useMutation({
    mutationFn: async (dto: {
      name: string
      ownerEmail: string
      ownerFirstName: string
      ownerLastName: string
      ownerPassword?: string
    }) => {
      return adminApi.createCompany({
        name: dto.name.trim(),
        owner: {
          email: dto.ownerEmail,
          firstName: dto.ownerFirstName,
          lastName: dto.ownerLastName,
          ...(dto.ownerPassword ? { password: dto.ownerPassword } : {}),
        },
      })
    },
    onSuccess: (_data, dto) => {
      reset()
      toast.success(dto.ownerPassword ? 'Company and owner created' : 'Company created — invitation sent to owner')
      onSuccess()
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message
      toast.error(typeof msg === 'string' ? msg : 'Failed to create company owner')
    },
  })

  const submitDisabled = mutation.isPending || !isValid || nameStatus !== 'available'

  return (
    <form onSubmit={handleSubmit(d => mutation.mutate(d))} className="space-y-3">
      <div>
        <label className="label">Company Name *</label>
        <input
          {...register('name', { required: true, validate: v => v.trim().length >= 2 || 'At least 2 characters' })}
          className="input"
          autoComplete="off"
        />
        <NameStatusHint status={nameStatus} />
      </div>
      <p className="text-xs font-medium text-gray-500 pt-1">Company Owner</p>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="label">First Name *</label><input {...register('ownerFirstName', { required: true })} className="input" /></div>
        <div><label className="label">Last Name *</label><input {...register('ownerLastName', { required: true })} className="input" /></div>
      </div>
      <div><label className="label">Email *</label><input {...register('ownerEmail', { required: true })} type="email" className="input" /></div>
      <div>
        <label className="label">Password <span className="text-gray-400 font-normal">(optional)</span></label>
        <input {...register('ownerPassword', { minLength: { value: 6, message: 'At least 6 characters' } })} type="password" className="input" placeholder="Leave blank to send invite email" />
        {errors.ownerPassword && <p className="text-xs text-red-600 mt-1">{errors.ownerPassword.message}</p>}
        <p className="text-xs text-gray-500 mt-0.5">Leave blank to send the owner an invitation link instead.</p>
      </div>
      <div className="flex justify-end pt-2">
        <button type="submit" disabled={submitDisabled} className="btn btn-primary btn-sm">
          {mutation.isPending ? 'Creating...' : 'Create Company'}
        </button>
      </div>
    </form>
  )
}

function NameStatusHint({ status }: { status: 'idle' | 'tooShort' | 'checking' | 'available' | 'taken' | 'error' }) {
  if (status === 'idle') return null
  if (status === 'tooShort') return <p className="text-xs text-gray-500 mt-1">At least 2 characters.</p>
  if (status === 'checking') return <p className="text-xs text-gray-400 mt-1">Checking availability…</p>
  if (status === 'available') return <p className="text-xs text-green-700 mt-1">✓ Available</p>
  if (status === 'taken') return <p className="text-xs text-red-600 mt-1">Company name already in use</p>
  return <p className="text-xs text-amber-600 mt-1">Couldn't verify availability — try again</p>
}
